export type ContentBlockType = 
  | 'heading'
  | 'paragraph' 
  | 'image'
  | 'video'
  | 'podcast'
  | 'quote'
  | 'statistics'
  | 'comparison-columns'
  | 'case-study-read-card'
  | 'bullet-list'
  | 'blog-block'
  | 'cta-button'
  | 'button-row'
  | 'spacer'
  | 'banner'
  | 'share-and-author-info'
  | 'sources';

export interface BaseContentBlock {
  id?: string;
  type: ContentBlockType;
}

export interface HeadingBlock extends BaseContentBlock {
  type: 'heading';
  level: 1 | 2 | 3 | 4 | 5 | 6;
  text: string;
  className?: string;
}

export interface ParagraphBlock extends BaseContentBlock {
  type: 'paragraph';
  text: string;
  className?: string;
}

export interface ImageBlock extends BaseContentBlock {
  type: 'image';
  src: string;
  alt: string;
  width?: number;
  height?: number;
  caption?: string;
  className?: string;
}

export interface VideoBlock extends BaseContentBlock {
  type: 'video';
  url: string;
  title?: string;
  platform?: 'cloudflare' | 'local';
}

export interface PodcastBlock extends BaseContentBlock {
  type: 'podcast';
  embedUrl: string;
  title?: string;
  description?: string;
  platform: 'spotify' | 'apple' | 'custom';
}

export interface QuoteBlock extends BaseContentBlock {
  type: 'quote';
  text: string;
  author: string;
  title?: string;
  image?: string;
  logo?: string;
}

export interface StatisticsBlock extends BaseContentBlock {
  type: 'statistics';
  stats: Array<{
    value: string;
    title?: string;
    description?: string;
    color?: string;
    hasGradient?: boolean;
    gradientFrom?: string;
    gradientTo?: string;
  }>;
  layout: 'grid' | 'carousel' | 'outcome-cards';
}

export interface ComparisonColumnsBlock extends BaseContentBlock {
  type: 'comparison-columns';
  leftTitle: string;
  leftText: string;
  rightTitle: string;
  rightText: string;
}

export interface CaseStudyReadCardBlock extends BaseContentBlock {
  type: 'case-study-read-card';
  title: string;
  href: string;
  imageSrc: string;
  imageAlt: string;
  ctaText?: string;
}

export interface BulletListBlock extends BaseContentBlock {
  type: 'bullet-list';
  items: string[];
  style?: 'default' | 'numbered' | 'checkmarks' | 'subpoints';
}

export interface BlogBlock extends BaseContentBlock {
  type: 'blog-block';
  title?: string;
  subtitle?: string;
  bodyText?: string;
  value?: string;
  description?: string;
  color?: string;
  rightImage?: string;
  hideCircle?: boolean;
  bgColor?: string;
  layout?: 'two-col' | 'three-col';
  hasGradient?: boolean;
  gradientFrom?: string;
  gradientTo?: string;
  hasGrainyEffect?: boolean;
  showArrow?: boolean;
}

export interface CTAButtonBlock extends BaseContentBlock {
  type: 'cta-button';
  text: string;
  href?: string;
  variant?: 'primary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  showModal?: 'eab-case-study' | 'perspective-piece';
}

export interface ButtonRowBlock extends BaseContentBlock {
  type: 'button-row';
  buttons: CTAButtonBlock[];
}

export interface SpacerBlock extends BaseContentBlock {
  type: 'spacer';
  height: 'sm' | 'md' | 'lg' | 'xl';
}

export interface BannerBlock extends BaseContentBlock {
  type: 'banner';
  title: string;
  description: string;
  authorName: string;
  authorTitle?: string;
  authorImage: string;
  publishDate: string;
  readingTime: string;
  articleImage: string;
  articleImageAlt: string;
  variant?: 'default' | 'with-svg' | 'article' | 'article-compact' | 'article-compact-split';
}

export interface ShareAndAuthorInfoBlock extends BaseContentBlock {
  type: 'share-and-author-info';
  authorImageUrl?: string;
  editorialInfo?: string;
}

export interface SourcesBlock extends BaseContentBlock {
  type: 'sources';
  items: Array<{
    id: string;
    title: string;
    authors?: string[];
    date?: string;
    url?: string;
    type: 'website' | 'book' | 'journal' | 'article' | 'other';
    publication?: string;
    doi?: string;
    isbn?: string;
    pages?: string;
    volume?: string;
    issue?: string;
    publisher?: string;
    accessDate?: string;
    description?: string;
  }>;
  title?: string;
  showViewAll?: boolean;
}

export type ContentBlock = 
  | HeadingBlock
  | ParagraphBlock
  | ImageBlock
  | VideoBlock
  | PodcastBlock
  | QuoteBlock
  | StatisticsBlock
  | ComparisonColumnsBlock
  | CaseStudyReadCardBlock
  | BulletListBlock
  | BlogBlock
  | CTAButtonBlock
  | ButtonRowBlock
  | SpacerBlock
  | BannerBlock
  | ShareAndAuthorInfoBlock
  | SourcesBlock;

// Helper function to create default blocks
export function create_default_block(type: ContentBlockType): ContentBlock {
  const baseBlock = {
    id: crypto.randomUUID(),
    type
  };

  switch (type) {
    case 'heading':
      return {
        ...baseBlock,
        type: 'heading',
        level: 2,
        text: 'New Heading'
      } as HeadingBlock;

    case 'paragraph':
      return {
        ...baseBlock,
        type: 'paragraph',
        text: 'Enter your paragraph text here...'
      } as ParagraphBlock;

    case 'image':
      return {
        ...baseBlock,
        type: 'image',
        src: '/placeholder-image.jpg',
        alt: 'Image description',
        width: 800,
        height: 450
      } as ImageBlock;

    case 'video':
      return {
        ...baseBlock,
        type: 'video',
        url: '',
        title: 'Video Title',
        platform: 'cloudflare'
      } as VideoBlock;

    case 'podcast':
      return {
        ...baseBlock,
        type: 'podcast',
        embedUrl: '',
        title: 'Podcast Title',
        platform: 'spotify'
      } as PodcastBlock;

    case 'quote':
      return {
        ...baseBlock,
        type: 'quote',
        text: 'Quote text here...',
        author: 'Author Name',
        title: 'Author Title'
      } as QuoteBlock;

    case 'statistics':
      return {
        ...baseBlock,
        type: 'statistics',
        stats: [
          {
            value: '95%',
            title: 'Success Rate',
            description: 'Student satisfaction',
            hasGradient: true,
            gradientFrom: 'from-blue-500',
            gradientTo: 'to-purple-600'
          }
        ],
        layout: 'grid'
      } as StatisticsBlock;

    case 'comparison-columns':
      return {
        ...baseBlock,
        type: 'comparison-columns',
        leftTitle: 'Before',
        leftText: 'Content for the before state.',
        rightTitle: 'After',
        rightText: 'Content for the after state.'
      } as ComparisonColumnsBlock;

    case 'case-study-read-card':
      return {
        ...baseBlock,
        type: 'case-study-read-card',
        title: 'Related case study',
        href: '/about/case-studies',
        imageSrc: '/placeholder-image.jpg',
        imageAlt: 'Related case study image',
        ctaText: 'Read more'
      } as CaseStudyReadCardBlock;

    case 'bullet-list':
      return {
        ...baseBlock,
        type: 'bullet-list',
        items: ['First item', 'Second item', 'Third item'],
        style: 'default'
      } as BulletListBlock;

    case 'blog-block':
      return {
        ...baseBlock,
        type: 'blog-block',
        title: 'Block Title',
        subtitle: 'Block Subtitle',
        bodyText: 'Block content here...',
        layout: 'two-col'
      } as BlogBlock;

    case 'cta-button':
      return {
        ...baseBlock,
        type: 'cta-button',
        text: 'Call to Action',
        href: '/book-a-demo',
        variant: 'primary',
        size: 'lg'
      } as CTAButtonBlock;

    case 'spacer':
      return {
        ...baseBlock,
        type: 'spacer',
        height: 'md'
      } as SpacerBlock;

    case 'banner':
      return {
        ...baseBlock,
        type: 'banner',
        title: 'Article Title',
        description: 'Article description goes here...',
        authorName: 'Author Name',
        authorImage: '/authors/default.jpg',
        publishDate: new Date().toISOString().split('T')[0],
        readingTime: '5 min read',
        articleImage: '/placeholder-banner.jpg',
        articleImageAlt: 'Article banner image',
        variant: 'article'
      } as BannerBlock;

    case 'share-and-author-info':
      return {
        ...baseBlock,
        type: 'share-and-author-info',
        authorImageUrl: '/team/jonathan.png',
        editorialInfo: 'StudyStash is the only adaptive learning intelligence built on proven neuroscience. We deliver measurable, evidence-backed improvements in student attainment and retention, reduced faculty workload, and university outcomes that matter.'
      } as ShareAndAuthorInfoBlock;

    case 'sources':
      return {
        ...baseBlock,
        type: 'sources',
        title: 'Sources & References',
        items: [],
        showViewAll: false
      } as SourcesBlock;

    default:
      throw new Error(`Unknown block type: ${type}`);
  }
}

// Helper function to add id and order to blocks
export function add_block_metadata(block: ContentBlock, index: number): ContentBlock & { id: string; order: number } {
  // Generate deterministic ID based on block content and index
  let id: string;
  
  if (block.type === 'heading') {
    // For headings, create ID from the text content
    const text = (block as any).text || '';
    id = `heading-${text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`;
  } else {
    // For other blocks, use type and index
    id = `block-${block.type}-${index}`;
  }
  
  return {
    ...block,
    id,
    order: index + 1
  };
}

// Helper function to add metadata to an array of blocks
export function add_blocks_metadata(blocks: ContentBlock[]): (ContentBlock & { id: string; order: number })[] {
  return blocks.map((block, index) => add_block_metadata(block, index));
}
